'use client'

import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

// Get the access token from environment variables
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN

// Check if token exists and set it
if (!MAPBOX_TOKEN) {
  console.error('Mapbox access token is missing')
} else {
  mapboxgl.accessToken = MAPBOX_TOKEN
}

interface PhysicianLocationMapProps {
  address: string
  physicianName: string
}

export const PhysicianLocationMap = ({
  address,
  physicianName,
}: PhysicianLocationMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const [, setCoordinates] = useState<[number, number] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Function to geocode address and initialize map
    const initializeMap = async () => {
      // Check if we have the access token
      if (!MAPBOX_TOKEN) {
        setError('Mapbox access token is missing')
        return
      }

      try {
        // Geocode the address using Mapbox Geocoding API
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            address
          )}.json?access_token=${MAPBOX_TOKEN}`
        )

        if (!response.ok) {
          throw new Error(`Geocoding failed: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center
          setCoordinates([lng, lat])

          if (mapContainer.current) {
            // Initialize map
            map.current = new mapboxgl.Map({
              container: mapContainer.current,
              style: 'mapbox://styles/mapbox/streets-v12',
              center: [lng, lat],
              zoom: 14,
            })

            // Add marker
            new mapboxgl.Marker()
              .setLngLat([lng, lat])
              .setPopup(
                new mapboxgl.Popup().setHTML(`
                  <strong>${physicianName}</strong><br/>
                  ${address}
                `)
              )
              .addTo(map.current)

            // Add navigation controls
            map.current.addControl(new mapboxgl.NavigationControl())
          }
        } else {
          setError('Location not found')
        }
      } catch (error) {
        console.error('Error initializing map:', error)
        setError('Error loading map')
      }
    }

    if (!map.current) {
      initializeMap()
    }

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [address, physicianName])

  if (error) {
    return (
      <div className='w-full h-[300px] rounded-lg bg-gray-100 flex items-center justify-center'>
        <p className='text-gray-500'>{error}</p>
      </div>
    )
  }

  return (
    <div
      ref={mapContainer}
      data-testid='location-map'
      className='w-full h-[300px] rounded-lg print:h-[170px]'
      style={{ border: '1px solid #e5e7eb' }}
    />
  )
}
